import React, { Component } from 'react';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Alert } from 'reactstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import { handlePhoneInput, getDigitsOnly } from '../../utils/phoneUtils';
import './contact-us.css';

export class ContactUs extends Component {
    static displayName = ContactUs.name;

    constructor(props) {
        super(props);
        this.state = {
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: '',
            captchaToken: '',
            isSubmitting: false,
            submitStatus: null,
            submitMessage: ''
        };
        this.recaptchaRef = React.createRef();
    }

    handleInputChange = (event) => {
        const { name, value } = event.target;
        this.setState({ [name]: value });
    };

    handleCaptchaChange = (token) => {
        this.setState({ captchaToken: token });
    };

    handleSubmit = async (event) => {
        event.preventDefault();
        this.setState({ isSubmitting: true, submitStatus: null });

        if (!this.state.captchaToken) {
            this.setState({
                isSubmitting: false,
                submitStatus: 'error',
                submitMessage: 'Please complete the captcha verification.'
            });
            return;
        }

        const formData = {
            name: this.state.name,
            email: this.state.email,
            phone: getDigitsOnly(this.state.phone),
            subject: this.state.subject,
            message: this.state.message,
            captchaToken: this.state.captchaToken
        };

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (response.ok && result.success) {
                // Reset captcha and form
                if (this.recaptchaRef.current) {
                    this.recaptchaRef.current.reset();
                }
                this.setState({
                    submitStatus: 'success',
                    submitMessage: result.message,
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: '',
                    captchaToken: ''
                });
            } else {
                this.setState({
                    submitStatus: 'error',
                    submitMessage: result.message || 'Failed to send your inquiry. Please try again.'
                });
            }
        } catch (error) {
            this.setState({
                submitStatus: 'error',
                submitMessage: 'Failed to send your inquiry. Please check your connection and try again.'
            });
        } finally {
            this.setState({ isSubmitting: false });
        }
    };

    render() {
        const { name, email, phone, subject, message, isSubmitting, submitStatus, submitMessage } = this.state;

        return (
            <div className='contact-us-page'>
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} md={10}>
                            <div className="contact-us-header text-center mb-4">
                                <h1 className="contact-title">CONTACT US</h1>
                                <p className="contact-subtitle">
                                    Have a question or inquiry? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                                </p>
                            </div>

                            {submitStatus && (
                                <Alert color={submitStatus === 'success' ? 'success' : 'danger'} className="mb-4">
                                    {submitMessage}
                                </Alert>
                            )}

                            <Form onSubmit={this.handleSubmit} className="contact-form">
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="name" className="form-label">Full Name *</Label>
                                            <Input
                                                type="text"
                                                name="name"
                                                id="name"
                                                value={name}
                                                onChange={this.handleInputChange}
                                                required
                                                maxLength={100}
                                                className="form-input"
                                                placeholder="Enter your full name"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="email" className="form-label">Email Address *</Label>
                                            <Input
                                                type="email"
                                                name="email"
                                                id="email"
                                                value={email}
                                                onChange={this.handleInputChange}
                                                required
                                                maxLength={255}
                                                className="form-input"
                                                placeholder="Enter your email address"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="phone" className="form-label">Phone Number</Label>
                                            <Input
                                                type="tel"
                                                name="phone"
                                                id="phone"
                                                value={phone}
                                                onChange={(e) => handlePhoneInput(e, this.setState.bind(this), 'phone')}
                                                maxLength={14}
                                                className="form-input"
                                                placeholder="(555) 123-4567"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="subject" className="form-label">Subject *</Label>
                                            <Input
                                                type="text"
                                                name="subject"
                                                id="subject"
                                                value={subject}
                                                onChange={this.handleInputChange}
                                                required
                                                maxLength={200}
                                                className="form-input"
                                                placeholder="Enter the subject of your inquiry"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <FormGroup>
                                    <Label for="message" className="form-label">Message *</Label>
                                    <Input
                                        type="textarea"
                                        name="message"
                                        id="message"
                                        value={message}
                                        onChange={this.handleInputChange}
                                        required
                                        maxLength={2000}
                                        rows={6}
                                        className="form-input"
                                        placeholder="Enter your message here..."
                                    />
                                    <small className="character-count">
                                        {message.length}/2000 characters
                                    </small>
                                </FormGroup>

                                <FormGroup className="text-center mb-4">
                                    <ReCAPTCHA
                                        ref={this.recaptchaRef}
                                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                        onChange={this.handleCaptchaChange}
                                        theme="light"
                                        size="normal"
                                        className="recaptcha-widget"
                                    />
                                </FormGroup>

                                <div className="text-center">
                                    <Button
                                        type="submit"
                                        size="lg"
                                        disabled={isSubmitting}
                                        className="submit-button"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Sending...
                                            </>
                                        ) : (
                                            'Send Message'
                                        )}
                                    </Button>
                                </div>
                            </Form>

                            <div className="contact-info mt-5 text-center">
                                <p className="direct-email">
                                    You can also email us directly at{' '}
                                    <a href="mailto:themagicalvapors@gmail.com" className="email-link">
                                        themagicalvapors@gmail.com
                                    </a>
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }
}
